import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AngularFirestore, DocumentData, DocumentReference } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, finalize, map, take, tap } from 'rxjs/operators';
import { RoleModel } from 'src/app/shared/models/role.model';
import {
    COMPANY_LEADS_COLLECTION,
    DEFAULT_LANGUAGE,
    EMPLOYEES_COLLECTION,
    USER_LOCAL_STORAGE_KEY
} from '../../app.constants';
import { LoggedInUser } from '../../shared/models/user.model';
import { BaseService } from './base.service';
import { FireLoggingService } from './fire-logging.service';
import { LanguageService } from './language.service';
import { StorageService } from './storage.service';

export interface IBrowserClientData {
    ip: string;
    createdAt: Date;
    userAgent: string;
}

const getChildValues = async (reference: DocumentReference<DocumentData>) => {
    const value = (await reference.get()).data();
    delete value.createdBy;
    return { ...value, id: value.id };
};

@Injectable({
    providedIn: 'root'
})
export class UserService extends BaseService {

    public browserClientData: IBrowserClientData;
    private listenerChangesSubscription;
    private loggedInUser = new LoggedInUser();

    constructor(
        protected firestore: AngularFirestore,
        protected http: HttpClient,
        protected functions: AngularFireFunctions,
        protected fireLogging: FireLoggingService,
        private storageService: StorageService,
        private languageService: LanguageService,
        public afAuth: AngularFireAuth,
        private firebaseDb: AngularFireDatabase,
        private router: Router,
    ) {
        super(firestore, http, functions, fireLogging);
    }

    get loggedUser() {
        return this.loggedInUser;
    }

    set loggedUser(user) {
        this.loggedInUser = user;
    }

    getLoggedInUser() {
        return this.loggedInUser;
    }

    destroyAllUserObservables() {
        if (this.listenerChangesSubscription) {
            this.listenerChangesSubscription.unsubscribe();
        }
    }

    updateLanguage(newLang: string) {
        return new Promise((resolve, reject) => {
            if (!!this.loggedUser && !!this.loggedUser.id) {
                this.firestore.collection(this.getUserCollectionByRole()).doc(this.loggedUser.id).set({
                    lang: newLang
                }, { merge: true }).then(() => resolve(null)).catch((e) => reject(e))
                    .catch(error => {
                        const message = error.error ? error.error.message : error.message;
                        this.fireLogging.sendErrorLog(`[updateLanguage(user.service.ts)] An error occurred 
                        while updating item into collection ${this.getUserCollectionByRole()}/${this.loggedUser.id}, 
                        details: ${message || JSON.stringify(error)}`);
                    });
            } else {
                reject('User not logged in.');
            }
        });
    }

    getUserCollectionByRole() {
        if (this.loggedUser.roles.find((item) => item.employeeRole === true)) {
            return EMPLOYEES_COLLECTION;

        } else if (this.loggedUser.roles.find((item) => item.faqRole === true)) {
            return COMPANY_LEADS_COLLECTION;
        }
    }

    getLoggedUserPermissions() {
        const permissions = [];
        for (const role of this.loggedUser.roles) {
            for (const perm of role.permissions) {
                if (!permissions.includes(perm)) {
                    permissions.push(perm);
                }
            }
        }
        return permissions;
    }

    getStoredLoggedUser() {
        return this.storageService.get(USER_LOCAL_STORAGE_KEY);
    }

    isLogged(): Observable<boolean> {
        return this.afAuth.idTokenResult.pipe(
            map(
                (res) => {
                    return !!res &&
                        res.claims && res.claims.email &&
                        this.loggedUser && this.loggedUser.email &&
                        res.claims.email.toLowerCase() === this.loggedUser.email.toLowerCase() &&
                        this.isNotExpired(res);
                }
            )
        );
    }

    hasAnyAuthority(permissions: string[] | string): boolean {
        if (!this.isLogged() || !this.loggedUser || !this.loggedUser.roles) {
            return false;
        }
        if (!Array.isArray(permissions)) {
            permissions = [ permissions ];
        }

        const userPermissions = [];
        this.loggedUser.roles.forEach((role: RoleModel) => {
            role.permissions.forEach((permission) => {
                userPermissions.push(permission);
            });
        });
        return permissions.some((authority: string) => userPermissions.includes(authority));
    }

    /**
     * Listen for changes in the logged-in user.
     */
    listenForChangesAtUser(collection: string, userId?: string): Observable<LoggedInUser | any> {
        const id = userId || this.loggedUser.id;
        return this.firestore
            .collection(collection)
            .doc(id)
            .snapshotChanges()
            .pipe(
                take(1),
                map(data => {
                    const obj: any = data.payload.data();
                    return { id: data.payload.id, ref: data.payload.ref, ...obj };
                }),
                concatMap(this.buildUserObj),
                tap(data => {
                    this.loggedUser = data;
                }),
                finalize(() => {
                    this.storageService.save(USER_LOCAL_STORAGE_KEY, this.loggedUser);
                    this.languageService.changeLanguage(this.loggedUser.lang ? this.loggedUser.lang :
                        DEFAULT_LANGUAGE);
                    this.languageService.languageChangeEmitter.emit();
                    return this.loggedUser;
                }),
                catchError((error) => {
                    const message = error.error ? error.error.message : error.message;
                    this.fireLogging.sendErrorLog(`[listenForChangesAtUser(user.service.ts)] An error occurred
                     while getting item into collection ${collection}/${id}, details: ${message || JSON.stringify(error)}`);
                    return of({ error });
                })
            );
    }

    /**
     * Map the reference of sub-collections to the user (sub-collections: roles, subscription list...)
     * @param userData The user retrieved
     */
    async buildUserObj(userData: DocumentData) {
        const user = new LoggedInUser();
        user.id = userData.id;
        user.firstName = userData.firstName;
        user.email = userData.email || userData.companyEmail;
        user.lastName = userData.lastName;
        user.lang = userData.lang;
        user.companyId = userData.companyId;
        user.birthday = userData.birthday;
        user.status = userData.status;
        user.companyId = userData.companyId;
        user.emailVerified = userData.emailVerified;
        user.copExpired = userData.copExpired;
        user.phone = userData.phone;
        user.consultancyProcessAlreadyFinished = !!userData.lastConsultancyProcessFinishedDate;
        if (userData.roles) {
            await Promise.all(userData.roles.map(getChildValues)).then(
                (roleList) => (user.roles = roleList)
            );
        }
        return user;
    }

    private isNotExpired({ expirationTime }) {
        return new Date(expirationTime) > new Date();
    }
}
